// AES-128-CBC segment decryption for HLS downloads, built on Web Crypto.
//
// HLS AES-128 encrypts each segment with AES-128-CBC + PKCS#7 padding, which is
// exactly what `SubtleCrypto.decrypt({name:'AES-CBC'})` expects (it validates +
// strips the PKCS#7 pad). The IV is either pinned by the playlist's
// `#EXT-X-KEY:...,IV=0x...` or, when absent, derived from the segment's media
// sequence number as a 16-byte big-endian integer (per RFC 8216 §5.2).

import type { M3u8Key } from './parseM3u8';

/** Parse a hex string (optionally `0x`-prefixed) into bytes. */
export function hexToBytes(hex: string): Uint8Array {
  const clean = hex.startsWith('0x') || hex.startsWith('0X') ? hex.slice(2) : hex;
  const padded = clean.length % 2 === 0 ? clean : `0${clean}`;
  const out = new Uint8Array(padded.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = parseInt(padded.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/** Build a 16-byte big-endian IV from a media sequence number. */
export function ivFromSequence(seq: number): Uint8Array {
  const iv = new Uint8Array(16);
  // Write the (up to) 53-bit safe-integer sequence into the low 8 bytes, BE.
  let value = seq;
  for (let i = 15; i >= 8 && value > 0; i -= 1) {
    iv[i] = value & 0xff;
    value = Math.floor(value / 256);
  }
  return iv;
}

/** Resolve the IV for a given segment: the pinned playlist IV, or derived. */
export function ivForSegment(key: M3u8Key, seq: number): Uint8Array {
  if (key.iv) return hexToBytes(key.iv);
  return ivFromSequence(seq);
}

/** Import raw 16-byte key material as an AES-CBC decrypt key. */
export function importAesKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw as BufferSource, { name: 'AES-CBC' }, false, ['decrypt']);
}

/** Decrypt one AES-128-CBC (PKCS#7-padded) segment. */
export async function decryptAes128Cbc(
  data: Uint8Array,
  key: CryptoKey,
  iv: Uint8Array,
): Promise<Uint8Array> {
  const plain = await crypto.subtle.decrypt({ name: 'AES-CBC', iv: iv as BufferSource }, key, data as BufferSource);
  return new Uint8Array(plain);
}

/** Fetch raw key bytes with bounded retries (mirrors the segment retry budget). */
export async function fetchKeyBytes(
  uri: string,
  fetchImpl: typeof fetch,
  attempts = 3,
  backoffMs = 300,
): Promise<Uint8Array> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const res = await fetchImpl(uri);
      if (!res.ok) throw new Error(`key fetch failed (${res.status})`);
      return new Uint8Array(await res.arrayBuffer());
    } catch (err) {
      lastErr = err;
      if (attempt < attempts && backoffMs > 0) {
        await new Promise((r) => setTimeout(r, backoffMs * attempt));
      }
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('key fetch failed');
}
